import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Phone, Globe, Clock, Loader2, ChevronRight, Car } from "lucide-react";
import api from "@/lib/api";
import { carPath } from "@/lib/carUrl";
import { resolveMediaUrl } from "@/lib/imageProxy";
import { formatPrice } from "@/lib/format";

/**
 * Gyorsnézet / adatlap — kereskedői profil összefoglaló + mini kínálat.
 */
export function ListingDealerPeek({ car, open, onNavigateAway, testid = "listing-dealer-peek" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !car?.id) {
      setData(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        let slug = car.seller_public?.slug;
        if (!slug) {
          const detail = await api.get(`/cars/${encodeURIComponent(car.id)}`);
          slug = detail.data?.seller_public?.slug;
        }
        if (!slug) {
          if (!cancelled) setData(null);
          return;
        }
        const res = await api.get(`/sellers/${encodeURIComponent(slug)}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, car?.id, car?.seller_public?.slug]);

  if (!open || (!loading && !data)) return null;

  const seller = data?.seller;
  const cars = data?.cars || [];
  const live = data?.stats?.live_listings ?? cars.length;
  const others = cars.filter((c) => c.id !== car.id).slice(0, 3);
  const location = [seller?.city, seller?.county].filter(Boolean).join(", ");
  const bioShort =
    seller?.bio && seller.bio.length > 160 ? `${seller.bio.slice(0, 157).trim()}…` : seller?.bio;

  return (
    <section
      data-testid={testid}
      className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden"
    >
      <div className="p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Hirdető</div>

        {loading && !seller ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-electric" /> Kereskedői profil…
          </div>
        ) : (
          seller && (
            <>
              <div className="flex gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-electric/15 border border-electric/25 shrink-0 overflow-hidden flex items-center justify-center">
                  {seller.picture ? (
                    <img src={seller.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-6 h-6 text-electric" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg font-medium text-white truncate">{seller.display_name}</div>
                  {seller.tagline && <p className="text-sm text-slate-400 mt-0.5 line-clamp-2">{seller.tagline}</p>}
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                      <Car className="w-3 h-3 text-electric" />
                      {live} autó kínálatban
                    </span>
                    {location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-electric" /> {location}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {bioShort && <p className="mt-3 text-sm text-slate-300 leading-relaxed">{bioShort}</p>}

              <ul className="mt-3 space-y-1.5 text-xs text-slate-400">
                {seller.opening_hours && (
                  <li className="flex gap-2">
                    <Clock className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" />
                    {seller.opening_hours}
                  </li>
                )}
                {seller.phone && (
                  <li className="flex gap-2">
                    <Phone className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" />
                    <a href={`tel:${seller.phone.replace(/\s/g, "")}`} className="hover:text-white">
                      {seller.phone}
                    </a>
                  </li>
                )}
                {seller.website && (
                  <li className="flex gap-2">
                    <Globe className="w-3.5 h-3.5 text-electric shrink-0 mt-0.5" />
                    <a
                      href={seller.website.startsWith("http") ? seller.website : `https://${seller.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-electric hover:underline truncate"
                    >
                      {seller.website.replace(/^https?:\/\//, "")}
                    </a>
                  </li>
                )}
              </ul>

              {others.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">További autók ettől a kereskedéstől</div>
                  <ul className="space-y-2">
                    {others.map((c) => (
                      <li key={c.id}>
                        <Link
                          to={carPath(c)}
                          onClick={() => onNavigateAway?.()}
                          data-testid={`dealer-peek-car-${c.id}`}
                          className="flex items-center gap-3 rounded-xl p-2 hover:bg-white/[0.05] transition-colors group"
                        >
                          <div className="w-14 h-10 rounded-lg overflow-hidden bg-white/5 shrink-0">
                            {c.image && (
                              <img
                                src={resolveMediaUrl(c.image)}
                                alt=""
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate group-hover:text-electric transition-colors">
                              {c.make} {c.model}
                            </div>
                            <div className="text-xs text-slate-500">{formatPrice(c.price)}</div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-electric shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Link
                to={`/kereskedes/${seller.slug}`}
                onClick={() => onNavigateAway?.()}
                data-testid="dealer-peek-full-profile"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-electric/40 bg-electric/10 hover:bg-electric/20 text-sm font-medium text-electric transition-colors"
              >
                Kereskedői profil és teljes kínálat
                <ChevronRight className="w-4 h-4" />
              </Link>
            </>
          )
        )}
      </div>
    </section>
  );
}
